// Pure game logic for Wordy Whiz: Stacks — no React, no DOM, easy to
// unit-test in isolation. A `grid` is a rows x cols array of arrays
// (its own shape is the source of truth for board size, so the same
// functions work for every mode's board), row 0 is the bottom (the
// "floor") and the last row is the top; a cell holds a single
// uppercase letter string or null.
//
// Design choices worth calling out:
// - Only the row and column that the just-dropped tile lands in are
//   ever scanned for a word. Every other line on the board was already
//   checked (and found word-free) on a previous turn and hasn't changed,
//   so this is both the fast path and the complete one for single-drop
//   matches.
// - No chain-reaction / combo detection after gravity collapses a
//   column: this is a turn-based puzzle, not a cascading match-3, so a
//   collapse settling into an incidental word goes uncaught until a
//   future drop happens to touch that same row or column.
// - A run can match reading in either direction (e.g. "STOP" / "POTS")
//   per the game's "either direction" rule.
// - Two match strategies (see findMatches): "substring" (Classic,
//   Blitz) clears the longest valid word within a run that contains the
//   dropped tile; "exact" (Challenge) only clears when the whole run is
//   itself a valid word.

import { MIN_WORD_LEN } from './constants';

export function createEmptyGrid(rows, cols) {
  return Array.from({ length: rows }, () => Array(cols).fill(null));
}

function gridRows(grid) { return grid.length; }
function gridCols(grid) { return grid[0].length; }

export function isColumnFull(grid, col) {
  return grid[gridRows(grid) - 1][col] !== null;
}

export function isBoardFull(grid) {
  return grid[gridRows(grid) - 1].every((cell) => cell !== null);
}

/** Drops `letter` into the lowest empty cell of `col`. Returns null if the column is full. */
export function dropLetter(grid, col, letter) {
  if (isColumnFull(grid, col)) return null;
  const next = grid.map((r) => r.slice());
  let row = 0;
  while (next[row][col] !== null) row++;
  next[row][col] = letter;
  return { grid: next, row, col };
}

// Reads `letters` forward and backward against `isValidWord`; returns
// whichever orientation is a real word, preferring the forward reading.
function validReading(letters, isValidWord) {
  const forward = letters.join('');
  if (isValidWord(forward)) return forward;
  const backward = letters.slice().reverse().join('');
  if (isValidWord(backward)) return backward;
  return null;
}

// Within one contiguous run of cells, finds the longest valid word (in
// either reading direction) that covers `idx` — the position of the
// newly dropped tile within that run. (Classic/Blitz.)
function longestMatchThrough(cells, idx, isValidWord, minWordLen) {
  let best = null;
  for (let start = 0; start <= idx; start++) {
    for (let end = idx; end < cells.length; end++) {
      const len = end - start + 1;
      if (len < minWordLen) continue;
      if (best && len <= best.cells.length) continue; // can't beat the current best
      const word = validReading(cells.slice(start, end + 1).map((c) => c.letter), isValidWord);
      if (word) best = { word, cells: cells.slice(start, end + 1) };
    }
  }
  return best;
}

// The entire run must itself be a valid word — no partial credit. (Challenge.)
function wholeRunMatch(cells, isValidWord, minWordLen) {
  if (cells.length < minWordLen) return null;
  const word = validReading(cells.map((c) => c.letter), isValidWord);
  return word ? { word, cells } : null;
}

/**
 * Checks the row and column through (row, col) for a newly formed word.
 * `matchStrategy` is "substring" (default) or "exact" — see file header.
 * `minWordLen` defaults to the game's usual MIN_WORD_LEN, but Drill mode
 * (practicing a specific dictionary) can pass a stricter threshold.
 * Returns an array of 0-2 matches: [{ direction, word, cells: [{row,col}] }].
 */
export function findMatches(grid, row, col, isValidWord, matchStrategy = 'substring', minWordLen = MIN_WORD_LEN) {
  const cols = gridCols(grid);
  const rows = gridRows(grid);
  const matches = [];

  let c0 = col;
  while (c0 > 0 && grid[row][c0 - 1] !== null) c0--;
  let c1 = col;
  while (c1 < cols - 1 && grid[row][c1 + 1] !== null) c1++;
  const hCells = [];
  for (let c = c0; c <= c1; c++) hCells.push({ row, col: c, letter: grid[row][c] });
  const hMatch = matchStrategy === 'exact'
    ? wholeRunMatch(hCells, isValidWord, minWordLen)
    : longestMatchThrough(hCells, col - c0, isValidWord, minWordLen);
  if (hMatch) {
    matches.push({
      direction: 'horizontal',
      word: hMatch.word,
      cells: hMatch.cells.map((c) => ({ row: c.row, col: c.col })),
    });
  }

  let r0 = row;
  while (r0 > 0 && grid[r0 - 1][col] !== null) r0--;
  let r1 = row;
  while (r1 < rows - 1 && grid[r1 + 1][col] !== null) r1++;
  const vCells = [];
  for (let r = r0; r <= r1; r++) vCells.push({ row: r, col, letter: grid[r][col] });
  const vMatch = matchStrategy === 'exact'
    ? wholeRunMatch(vCells, isValidWord, minWordLen)
    : longestMatchThrough(vCells, row - r0, isValidWord, minWordLen);
  if (vMatch) {
    matches.push({
      direction: 'vertical',
      word: vMatch.word,
      cells: vMatch.cells.map((c) => ({ row: c.row, col: c.col })),
    });
  }

  return matches;
}

/** Clears the given cells and lets each affected column's remaining letters fall to close the gap. */
export function clearAndCollapse(grid, cellsToClear) {
  const rows = gridRows(grid);
  const next = grid.map((r) => r.slice());
  for (const { row, col } of cellsToClear) next[row][col] = null;

  const affectedCols = new Set(cellsToClear.map((c) => c.col));
  for (const col of affectedCols) {
    const remaining = [];
    for (let row = 0; row < rows; row++) {
      if (next[row][col] !== null) remaining.push(next[row][col]);
    }
    for (let row = 0; row < rows; row++) {
      next[row][col] = row < remaining.length ? remaining[row] : null;
    }
  }
  return next;
}

export function scoreForMatches(matches) {
  let score = matches.reduce((sum, m) => sum + m.word.length * 10, 0);
  if (matches.length > 1) score += 25; // combo: cleared a word in both directions on one drop
  return score;
}

/**
 * Plays one full turn: drop, check, clear+collapse. Returns null if the
 * column was full (illegal move — the caller should ignore the click).
 */
export function playTurn(grid, col, letter, isValidWord, matchStrategy = 'substring', minWordLen = MIN_WORD_LEN) {
  const dropped = dropLetter(grid, col, letter);
  if (!dropped) return null;

  const matches = findMatches(dropped.grid, dropped.row, dropped.col, isValidWord, matchStrategy, minWordLen);
  const cellsToClear = matches.flatMap((m) => m.cells);
  const finalGrid = cellsToClear.length
    ? clearAndCollapse(dropped.grid, cellsToClear)
    : dropped.grid;

  return {
    grid: finalGrid,
    droppedAt: { row: dropped.row, col: dropped.col },
    matches,
    scoreDelta: scoreForMatches(matches),
    gameOver: isBoardFull(finalGrid),
  };
}
